import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, Circle, Rocket, TriangleAlert } from "lucide-react";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const errText = (e) =>
  e?.response?.data?.detail?.message || e?.response?.data?.detail?.reason || apiError(e);

// Human guidance for every readiness check id returned by the backend.
const GUIDE = {
  voice_agent_provider: {
    title: "Pick a live voice agent provider",
    why: "Vapi orchestrates the real AI agent, the LLM and the voice. Without it no call can be placed.",
    to: "/provider-center",
  },
  telephony_provider: {
    title: "Pick a live telephony provider",
    why: "Voiso, IllyVoIP, MMDSmart or Bitcall carries the actual phone call. Without a live carrier no audio ever leaves the platform.",
    to: "/provider-center",
  },
  voice_agent_credentials: {
    title: "Add the Vapi private API key",
    why: "Use the private key from dashboard.vapi.ai → API Keys. The public key cannot create assistants or calls.",
    to: "/provider-center",
  },
  telephony_credentials: {
    title: "Add the telephony carrier credentials",
    why: "Voiso needs api_key + cluster_id + SIP auth, IllyVoIP needs the API key + SIP identity, Bitcall needs the SIP username and password. Ask the carrier to enable SIP trunk termination.",
    to: "/provider-center",
  },
  voice_agent_health: {
    title: "Verify the Vapi connection",
    why: "A health check confirms the key is accepted and the account is active.",
    to: "/provider-center",
    healthFor: "voice",
  },
  telephony_health: {
    title: "Verify the carrier connection",
    why: "A health check confirms the carrier account is reachable and the credentials have the right scopes.",
    to: "/provider-center",
    healthFor: "telephony",
  },
  voice_agent_webhook: {
    title: "Register the Vapi webhook",
    why: "Call state, transcripts and cost land on /api/webhooks/vapi. Publishing an agent sets this automatically.",
    to: "/provider-center",
  },
  telephony_webhook: {
    title: "Register the carrier webhook",
    why: "Carriers that publish call events (Voiso, MMDSmart, Twilio) post ringing, answered and hangup with a signed secret. IllyVoIP and Bitcall have no call webhook — Vapi reports call state.",
    to: "/provider-center",
  },
  sip_trunk_linked: {
    title: "Link the carrier SIP trunk to Vapi",
    why: "Vapi dials out over your carrier trunk with username/password auth. Inbound carriers must also allowlist the Vapi signalling IPs and UDP 40000-60000 for RTP; Bitcall is outbound only.",
    to: "/provider-center",
    linkTrunk: true,
  },
  agent_published: {
    title: "Publish an AI agent",
    why: "Publishing pushes the script, language and ElevenLabs voice into a real Vapi assistant.",
    to: "/agents",
  },
  number_synced: {
    title: "Import an active phone number",
    why: "The caller ID your prospects will see. Import it from the provider and make sure it is synced.",
    to: "/phone-numbers",
  },
  number_in_voice_provider: {
    title: "Register the number inside Vapi",
    why: "Vapi needs the number attached to the trunk before it can place an outbound call with it.",
    to: "/phone-numbers",
  },
  budget: {
    title: "Set a campaign budget",
    why: "A total budget caps spend and lets the platform stop a campaign before it runs away.",
    to: "/campaigns/new",
  },
  dnc_disclosure: {
    title: "Compliance: DNC and AI disclosure",
    why: "Every live call screens the do-not-call list and the assistant discloses it is an AI.",
    to: "/do-not-call",
  },
};

function StepRow({ index, check, open, onToggle, children }) {
  const state = check.ok ? "ok" : check.blocking ? "blocked" : "warn";
  const guide = GUIDE[check.id] || {};
  return (
    <div
      data-testid={`setup-step-${check.id}`}
      className={cn(
        "border-b border-slate-200 last:border-0",
        state === "blocked" && !check.ok ? "bg-rose-50/40" : null
      )}
    >
      <button
        type="button"
        data-testid={`setup-step-toggle-${check.id}`}
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50"
      >
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white",
            state === "ok" ? "bg-emerald-500" : state === "blocked" ? "bg-rose-500" : "bg-amber-400"
          )}
        >
          {state === "ok" ? <Check className="h-3.5 w-3.5" /> : index}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-800">
            {guide.title || check.label}
          </span>
          <span className="block truncate text-[11px] text-slate-500">{check.label}</span>
        </span>
        {!check.ok && !check.blocking ? (
          <span className="hidden shrink-0 text-[11px] font-medium text-amber-600 sm:inline">Optional</span>
        ) : null}
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>
      {open ? (
        <div data-testid={`setup-step-body-${check.id}`} className="space-y-3 px-4 pb-4 pl-12">
          <p className="text-sm text-slate-600">{guide.why}</p>
          {check.action ? (
            <p className="text-xs font-medium text-slate-500">Next: {check.action}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">{children}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function Setup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [manualOpen, setManualOpen] = useState(null);

  const { data: readiness, isLoading } = useQuery({
    queryKey: ["pc-readiness"],
    queryFn: () => get("/provider-center/live-readiness"),
    refetchInterval: 20000,
  });

  const health = useMutation({
    mutationFn: (key) => api.post(`/provider-center/credentials/${key}/health`),
    onSuccess: () => { toast.success("Connection healthy"); queryClient.invalidateQueries(); },
    onError: (e) => toast.error(errText(e)),
  });

  const linkTrunk = useMutation({
    mutationFn: (key) => api.post(`/provider-center/${key}/link-vapi`),
    onSuccess: ({ data }) => {
      toast.success(`SIP trunk linked · ${data.sip_host} (${data.region})`, { duration: 9000 });
      toast.info(data.note, { duration: 12000 });
      queryClient.invalidateQueries();
    },
    onError: (e) => toast.error(errText(e)),
  });

  const checks = readiness?.checks || [];
  const done = checks.filter((c) => c.ok).length;
  const progress = checks.length ? Math.round((done / checks.length) * 100) : 0;
  const nextId = useMemo(() => checks.find((c) => !c.ok)?.id || null, [checks]);
  const openId = manualOpen !== null ? manualOpen : nextId;

  if (user?.role !== "super_admin")
    return (
      <div className="panel p-6" data-testid="setup-no-access">
        <h2 className="text-lg font-semibold text-slate-900">Super Admin only</h2>
        <p className="mt-1 text-sm text-slate-500">
          Going live requires provider credentials, so only a Super Admin can run the setup assistant.
        </p>
      </div>
    );

  return (
    <>
      <PageHeader
        testId="setup-page"
        title="Setup assistant"
        subtitle="Everything between an empty workspace and your first real outbound AI call"
        actions={
          <StatusBadge
            value={readiness?.ready ? "completed" : "paused"}
            label={readiness?.ready ? "Ready for live calls" : `${readiness?.blockers ?? 0} blocker(s)`}
            testId="setup-readiness-badge"
          />
        }
      />

      <Section
        testId="setup-progress"
        title="Go-live progress"
        subtitle={`${done} of ${checks.length} steps complete`}
      >
        <div className="p-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              data-testid="setup-progress-bar"
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {readiness?.ready
              ? "All blocking steps are done — place a live test call from any published agent."
              : "Finish the highlighted step, then the next one opens automatically."}
          </p>
        </div>
      </Section>

      <Section testId="setup-steps" title="Steps">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-slate-500">Loading readiness…</p>
        ) : (
          checks.map((check, i) => {
            const guide = GUIDE[check.id] || {};
            const providerKey =
              guide.healthFor === "voice"
                ? readiness?.voice_agent_provider
                : guide.healthFor === "telephony"
                ? readiness?.telephony_provider
                : null;
            return (
              <StepRow
                key={check.id}
                index={i + 1}
                check={check}
                open={openId === check.id}
                onToggle={() => setManualOpen(openId === check.id ? "" : check.id)}
              >
                {guide.to ? (
                  <Button
                    asChild
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid={`setup-go-${check.id}`}
                  >
                    <Link to={guide.to}>Open {guide.to.replace("/", "").replace("-", " ")}</Link>
                  </Button>
                ) : null}
                {providerKey ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={health.isPending}
                    onClick={() => health.mutate(providerKey)}
                    data-testid={`setup-health-${check.id}`}
                  >
                    Run health check
                  </Button>
                ) : null}
                {guide.linkTrunk ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={linkTrunk.isPending}
                    onClick={() => linkTrunk.mutate(readiness?.telephony_provider)}
                    data-testid="setup-link-trunk"
                  >
                    Link SIP trunk now
                  </Button>
                ) : null}
              </StepRow>
            );
          })
        )}
      </Section>

      <Section testId="setup-final" title="Final step · first live call">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            {readiness?.ready ? (
              <Rocket className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
            ) : (
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            )}
            <p className="text-sm text-slate-600">
              {readiness?.ready
                ? "Open a published agent and press Live test to dial your own number through your carrier + Vapi."
                : "Clear the blocking steps above — the live test button will reject the call until then."}
            </p>
          </div>
          <Button
            asChild
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="setup-go-live-test"
          >
            <Link to="/agents">Go to agents</Link>
          </Button>
        </div>
      </Section>
    </>
  );
}

export function SetupBanner() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["pc-readiness"],
    queryFn: () => get("/provider-center/live-readiness"),
    refetchInterval: 20000,
    enabled: user?.role === "super_admin",
  });
  if (user?.role !== "super_admin" || !data || data.ready) return null;
  const done = (data.checks || []).filter((c) => c.ok).length;
  return (
    <div
      data-testid="setup-banner"
      className="panel flex flex-col gap-3 border-l-4 border-l-amber-400 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <Circle className="mt-1 h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />
        <div>
          <p className="text-sm font-semibold text-slate-800">
            Not ready for live calls yet — {done}/{(data.checks || []).length} setup steps done
          </p>
          <p className="text-xs text-slate-500">
            {data.blockers} blocking step(s) left before your first real outbound AI call.
          </p>
        </div>
      </div>
      <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-600" data-testid="setup-banner-cta">
        <Link to="/setup">Open setup assistant</Link>
      </Button>
    </div>
  );
}